import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MoreHorizontal, X } from "lucide-react";
import { BRAND } from "@/lib/brand";
import UserMenu from "@/components/UserMenu";

import ModuleSwitcher from "@/components/ModuleSwitcher";
import { moduleFor, isNavActive as isActive } from "@/lib/modules";

export default function AppHeader({ right }) {
  const { pathname } = useLocation();
  const NAV = moduleFor(pathname).nav;  // sub-navigation of the current module (Sales / Purchases / P&L)
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => setMoreOpen(false), [pathname]);
  // Mobile: max one row — modules with many sections get a "More" sheet for the rest
  const overflow = NAV.length > 6;
  const primary = overflow ? NAV.slice(0, 5) : NAV;
  const more = overflow ? NAV.slice(5) : [];
  const moreActive = more.some((n) => isActive(pathname, n.to));
  const mobileItem = (n, testidPrefix = "m-") => {
    const active = isActive(pathname, n.to);
    return (
      <Link key={n.to} to={n.to} data-testid={`${testidPrefix}${n.testid}`} data-on={active} aria-current={active ? "page" : undefined}
        className="flex flex-col items-center justify-center gap-1 h-14 rounded-lg text-[10px] font-semibold text-slate-400 data-[on=true]:text-amber-300 transition-colors">
        <n.icon className="w-5 h-5 shrink-0" />
        <span className="leading-none">{n.en}</span>
      </Link>
    );
  };
  return (
    <>
      <header className="border-b border-slate-800/80 bg-[#0B0F17]/80 backdrop-blur-xl sticky top-0 z-30 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
          <Link to="/dashboard" className="flex items-center gap-3 min-w-0 flex-1" data-testid="brand-link">
            <div className="w-10 h-10 rounded-xl bg-white grid place-items-center shadow-lg shadow-sky-900/40 overflow-hidden shrink-0 ring-1 ring-sky-400/30">
              <img src={BRAND.emblem} alt="Hup Ley Huat logo" className="w-9 h-9 object-contain" data-testid="brand-logo" />
            </div>
            <div className="hidden lg:block min-w-0">
              <h1 className="font-heading text-base font-black tracking-tight leading-none truncate" data-testid="brand-name">{BRAND.fullName}</h1>
              <p className="font-zh text-[11px] text-sky-300/80 tracking-wide truncate">{BRAND.tagline}</p>
            </div>
          </Link>
          {NAV.length > 0 && <nav className="hidden md:flex items-center gap-1 rounded-xl border border-slate-800 bg-slate-900/60 p-1 shrink-0" aria-label="Main">
            {NAV.map((n) => {
              const active = isActive(pathname, n.to);
              return (
                <Link key={n.to} to={n.to} data-testid={n.testid} data-on={active} title={`${n.en} · ${n.zh}`} aria-current={active ? "page" : undefined}
                  className="flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-semibold text-slate-400 hover:text-slate-200 transition-colors whitespace-nowrap data-[on=true]:bg-amber-400 data-[on=true]:text-slate-950">
                  <n.icon className="w-4 h-4 shrink-0" />
                  <span className="hidden xl:inline">{n.en}</span>
                  <span className="font-zh text-xs hidden 2xl:inline opacity-80">{n.zh}</span>
                </Link>
              );
            })}
          </nav>}
          <div className="flex items-center gap-2 shrink-0 ml-auto">{right}<ModuleSwitcher /><UserMenu /></div>
        </div>
      </header>
      {moreOpen && <button type="button" aria-label="Close menu" onClick={() => setMoreOpen(false)} className="md:hidden fixed inset-0 z-20 bg-black/60" data-testid="mobile-nav-more-backdrop" />}
      {NAV.length > 0 && <nav className="mobile-nav md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-slate-800 bg-[#0B0F17]/95 backdrop-blur-xl print:hidden" aria-label="Main" data-testid="mobile-nav">
        {moreOpen && <>
          <div className="absolute bottom-full inset-x-0 border-t border-slate-700 bg-[#0F1522] rounded-t-2xl shadow-2xl p-3" data-testid="mobile-nav-more-sheet">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold px-1 pb-2">More</div>
            <div className="grid gap-1">
              {more.map((n) => {
                const active = isActive(pathname, n.to);
                return (
                  <Link key={n.to} to={n.to} data-testid={`m-${n.testid}`} data-on={active} aria-current={active ? "page" : undefined}
                    className="flex items-center gap-3 h-12 px-3 rounded-xl text-sm font-semibold text-slate-300 hover:bg-slate-800/70 data-[on=true]:bg-amber-400/15 data-[on=true]:text-amber-300 transition-colors">
                    <n.icon className="w-5 h-5 shrink-0" /><span>{n.en}</span><span className="font-zh text-xs text-slate-500 ml-auto">{n.zh}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </>}
        <div className="grid px-1 pb-[env(safe-area-inset-bottom)]" style={{ gridTemplateColumns: `repeat(${primary.length + (overflow ? 1 : 0)}, minmax(0, 1fr))` }}>
          {primary.map((n) => mobileItem(n))}
          {overflow && (
            <button type="button" onClick={() => setMoreOpen((v) => !v)} data-testid="m-nav-more" data-on={moreActive || moreOpen} aria-expanded={moreOpen}
              className="flex flex-col items-center justify-center gap-1 h-14 rounded-lg text-[10px] font-semibold text-slate-400 data-[on=true]:text-amber-300 transition-colors">
              {moreOpen ? <X className="w-5 h-5 shrink-0" /> : <MoreHorizontal className="w-5 h-5 shrink-0" />}
              <span className="leading-none">More</span>
            </button>
          )}
        </div>
      </nav>}
    </>
  );
}
